import React, { SyntheticEvent, useEffect, useRef, useState } from 'react'

import { Grid, Pagination, Icon, Label, PaginationProps, Button, Form, Checkbox, Dropdown } from 'semantic-ui-react'

import { IClip, SearchIncludingOptions } from '../../../api/Data'

import UpgradeTag from '../../../components/common/UpgradeTag'

import { formatNumber } from '../../../utils/sharedFunctions'

const CurateControlsBar = (props: IProps) => {
  const {
    activeTab,
    tags,
    page,
    totalTeamsClipCount,
    projectEntryCount,
    projectClipCount,
    clipMax,
    clipsPerPage,
    showPagination,
    onBulkTag,
    onSearch,
    onSelectAll,
    onBack,
    clipsSelected,
    searchIncluding,
    onSearchIncludingChange,
  } = props

  const { removeFilterTag, setPage, filterByFavorite } = props
  const [showSearchFilter, setShowSearchFilter] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSearchFilter(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef])

  const goToPage = (_e: SyntheticEvent, data: PaginationProps) => {
    const page = Number(data.activePage)
    setPage(page)
  }

  const onSearchEvent = (e: SyntheticEvent, data: any) => {
    onSearch(data.value)
  }

  const buttonStack = {
    display: 'inline-block',
    gap: '1rem',
  }

  const handleSearchFilterChange = (optionName: keyof SearchIncludingOptions) => {
    const newOptions = {
      ...searchIncluding,
      [optionName]: !searchIncluding[optionName],
    }
    onSearchIncludingChange(newOptions)
  }

  return (
    <Grid verticalAlign="middle" stackable>
      <Grid.Column width={4}>
        <div style={{ display: 'flex', width: '100%' }}>
          <Form.Input
            icon="search"
            placeholder="Search..."
            fluid
            inverted
            name="search"
            onChange={onSearchEvent}
            style={{
              width: '170px',
              margin: 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
          />
          <div
            ref={dropdownRef}
            style={{
              width: '100px',
              margin: 0,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              background:
                searchIncluding.contributorDetails || searchIncluding.tags || searchIncluding.clipAnswers
                  ? '#6b50ff'
                  : '#ffffff',
              color: showSearchFilter ? 'white' : 'black',
              borderLeft: '1px solid #ccc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <Dropdown
              placeholder="Filter"
              floating
              onClick={() => setShowSearchFilter(!showSearchFilter)}
              open={showSearchFilter}
            >
              <Dropdown.Menu style={{ left: 'auto', right: 0, minWidth: '200px' }}>
                <Dropdown.Item onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    label="Contributor details"
                    checked={searchIncluding.contributorDetails}
                    onChange={() => handleSearchFilterChange('contributorDetails')}
                  />
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    label="Tags"
                    checked={searchIncluding.tags}
                    onChange={() => handleSearchFilterChange('tags')}
                  />
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    label="Clips answers"
                    checked={searchIncluding.clipAnswers}
                    onChange={() => handleSearchFilterChange('clipAnswers')}
                  />
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Grid.Column>

      <Grid.Column width={6} style={buttonStack}>
        <Button compact basic size="mini" onClick={onSelectAll}>
          <Icon name="list" />
          {clipsSelected.length > 0 ? `Deselect (${clipsSelected.length})` : 'Select all'}
        </Button>
        <Button compact basic size="mini" onClick={filterByFavorite}>
          <Icon name="heart" color={activeTab === 'favorites' ? 'red' : 'grey'} />
          Favorites
        </Button>
        <Button compact primary size="mini" onClick={onBulkTag} disabled={clipsSelected.length <= 0}>
          <Icon name="tag" />
          Bulk tag
        </Button>
      </Grid.Column>

      {showPagination ? (
        <>
          <Grid.Column width={6} textAlign="right">
            <span style={{ marginRight: '1rem', color: `${totalTeamsClipCount >= clipMax ? 'red' : 'grey'}` }}>
              <strong>{projectClipCount === 1 ? '1 clip ' : `${formatNumber(projectClipCount)} clips `}</strong> in {projectEntryCount === 1 ? '1 submission' : `${formatNumber(projectEntryCount)} submissions`}
              {totalTeamsClipCount >= clipMax && <UpgradeTag text="Upgrade your plan to collect more video clips" />}
            </span>

            {/* Pagination */}
            <Pagination
              size="mini"
              boundaryRange={0}
              defaultActivePage={page}
              ellipsisItem={null}
              firstItem={null}
              lastItem={null}
              siblingRange={1}
              onPageChange={goToPage}
              totalPages={Math.ceil(projectEntryCount! / clipsPerPage)}
            />
          </Grid.Column>
        </>
      ) : (
        <Grid.Column width={6} textAlign="right" style={buttonStack}>
          <Button compact secondary basic size="mini" onClick={onBack}>
            <Icon name="arrow left" />
            Back
          </Button>
        </Grid.Column>
      )}

      {tags.length > 0 && (
        <Grid.Column width={16}>
          <div>
            <p style={{ marginRight: '1rem', display: 'inline' }}>Search by tags:</p>
            <Label.Group size="large" style={{ display: 'inline' }}>
              {tags.map((tag) => (
                <Label basic color="violet" key={tag}>
                  {tag}
                  <Icon name="delete" onClick={() => removeFilterTag(tag)} />
                </Label>
              ))}
            </Label.Group>
          </div>
        </Grid.Column>
      )}
    </Grid>
  )
}

interface IProps {
  activeTab: string
  tags: string[]
  totalTeamsClipCount: number
  projectEntryCount: number
  projectClipCount: number
  clipMax: number
  page: number
  clipsPerPage: number
  showPagination: boolean
  maxNbOfClips: number
  removeFilterTag: (arg: string) => void
  setPage: (arg: number) => void
  filterByFavorite: () => void
  onBulkTag: () => void
  onSearch: (arg: string) => void
  onSelectAll: () => void
  onBack?: () => void
  clipsSelected: IClip[]
  searchIncluding: SearchIncludingOptions
  onSearchIncludingChange: (newOptions: SearchIncludingOptions) => void
}

export default CurateControlsBar
